import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import type {
  SupervisorSession,
  SupervisorAction,
  ActiveCallMonitoring,
  CreateSupervisorSessionRequest,
  UpdateSupervisorSessionRequest,
  CreateSupervisorActionRequest,
  SupervisorActiveSession,
} from '../types/supervisor-monitoring';

export class SupervisorMonitoringRepository {
  protected client: SupabaseClient;

  constructor() {
    this.client = supabase;
  }
  // ============================================================================
  // SUPERVISOR SESSIONS
  // ============================================================================

  async getActiveSessions(companyId: string): Promise<SupervisorSession[]> {
    const { data, error } = await this.client
      .from('supervisor_sessions')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getSessionById(id: string): Promise<SupervisorSession | null> {
    const { data, error } = await this.client
      .from('supervisor_sessions')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  async createSession(
    request: CreateSupervisorSessionRequest
  ): Promise<SupervisorSession> {
    const { data, error } = await this.client
      .from('supervisor_sessions')
      .insert(request)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateSession(
    id: string,
    updates: UpdateSupervisorSessionRequest
  ): Promise<SupervisorSession> {
    const { data, error } = await this.client
      .from('supervisor_sessions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async endSession(id: string): Promise<SupervisorSession> {
    const { data, error } = await this.client
      .from('supervisor_sessions')
      .update({
        is_active: false,
        ended_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getSupervisorActiveSessions(
    supervisorId: string
  ): Promise<SupervisorActiveSession[]> {
    const { data, error } = await this.client.rpc(
      'get_supervisor_active_sessions',
      {
        p_supervisor_id: supervisorId,
      }
    );

    if (error) throw error;
    return data || [];
  }

  // ============================================================================
  // SUPERVISOR ACTIONS
  // ============================================================================

  async getSessionActions(sessionId: string): Promise<SupervisorAction[]> {
    const { data, error } = await this.client
      .from('supervisor_actions')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async createAction(
    request: CreateSupervisorActionRequest
  ): Promise<SupervisorAction> {
    const { data, error } = await this.client
      .from('supervisor_actions')
      .insert(request)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async logAction(
    sessionId: string,
    actionType: string,
    fromState?: string | null,
    toState?: string | null,
    notes?: string | null,
    metadata?: Record<string, any>
  ): Promise<string> {
    const { data, error } = await this.client.rpc('log_supervisor_action', {
      p_session_id: sessionId,
      p_action_type: actionType,
      p_from_state: fromState,
      p_to_state: toState,
      p_notes: notes,
      p_metadata: metadata || {},
    });

    if (error) throw error;
    return data;
  }

  // ============================================================================
  // ACTIVE CALLS MONITORING
  // ============================================================================

  async getActiveCallsForMonitoring(
    companyId: string
  ): Promise<ActiveCallMonitoring[]> {
    const { data, error } = await this.client
      .from('active_calls_monitoring')
      .select('*')
      .eq('company_id', companyId);

    if (error) throw error;
    return data || [];
  }

  async getCallDetails(callId: string): Promise<ActiveCallMonitoring | null> {
    const { data, error } = await this.client
      .from('active_calls_monitoring')
      .select('*')
      .eq('call_id', callId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToActiveCalls(
    companyId: string,
    callback: (payload: any) => void
  ): () => void {
    const channel = this.client
      .channel(`active_calls_${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls',
          filter: `company_id=eq.${companyId}`,
        },
        callback
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }

  subscribeToSupervisorSessions(
    companyId: string,
    callback: (payload: any) => void
  ): () => void {
    const channel = this.client
      .channel(`supervisor_sessions_${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'supervisor_sessions',
          filter: `company_id=eq.${companyId}`,
        },
        callback
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }
}

export const supervisorMonitoringRepository = new SupervisorMonitoringRepository();
